import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { LogLevels, OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { isValidCSharpInt } from 'src/app/common-components/helpers/csharp-max-integer-helper';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { AccountOrderingOptions, AccountRegisterOptions } from '../models/account-register-options';
import { AccountsFilterOption } from '../models/accounts-filter-option';
import { getAccountIdsQuery, getAccountsQuery } from '../queries/accounts.graphql';
import { GetAccountIds, GetAccountIdsVariables } from '../queries/generated/GetAccountIds';
import { GetAccounts, GetAccountsVariables } from '../queries/generated/GetAccounts';

@Injectable({ providedIn: 'root' })
export class AccountRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getAccounts(
      endCursor: string | null,
      filters: AccountsFilterOption[] | null,
      quickFilters: AccountRegisterOptions | null,
      orderingOptions: AccountOrderingOptions[] | null
   ): Observable<ExecutionResult<GetAccounts>> {
      const filterExpression = this.constructFilterExpression(filters, quickFilters);
      const variables = this.getAccountsVariables(endCursor, filterExpression, orderingOptions);

      return this.apiService.query<GetAccounts, GetAccountsVariables>({
         query: getAccountsQuery,
         variables,
      });
   }

   getAccountIds(
      endCursor: string | null,
      filters: AccountsFilterOption[] | null,
      quickFilters: AccountRegisterOptions | null,
      orderingOptions: AccountOrderingOptions[] | null
   ): Observable<ExecutionResult<GetAccountIds>> {
      const filterExpression = this.constructFilterExpression(filters, quickFilters);
      const variables = this.getAccountsVariables(endCursor, filterExpression, orderingOptions);

      return this.apiService.query<GetAccountIds, GetAccountIdsVariables>({
         query: getAccountIdsQuery,
         variables,
      });
   }

   private constructFilterExpression(
      filters: AccountsFilterOption[] | null,
      quickFilters: AccountRegisterOptions | null
   ): FilterExpression {
      let filterOptions: FilterOption[] = [];

      if (!quickFilters?.showAllAccounts) {
         filterOptions = filters?.map(this.convertUserFilterOption.bind(this)) ?? [];
      }

      const filterExpression: FilterExpression = {
         filterOptions,
      };

      if (!quickFilters) return filterExpression;

      if (quickFilters.searchByLogLevel) {
         filterExpression.filterOptions.push({
            field: 'LogLevelId',
            operation: FilterOperations.NotEqualTo,
            value: LogLevels.Info,
         });
      }

      if (quickFilters.internalOptions) {
         const internalOptions = quickFilters.internalOptions;
         if (internalOptions === 'Internal Accounts Only') {
            filterExpression.filterOptions.push({
               field: 'isLcsInternal',
               operation: FilterOperations.EqualTo,
               value: true,
            });
         } else if (internalOptions === 'Customer Accounts Only') {
            filterExpression.filterOptions.push({
               field: 'isLcsInternal',
               operation: FilterOperations.EqualTo,
               value: false,
            });
         }
      }

      const searchText = quickFilters.searchText?.trim();
      if (searchText) {
         const subExpression: FilterExpression = {
            filterOptions: [
               { field: 'Name', operation: FilterOperations.Contains, value: searchText },
               { field: 'ShortName', operation: FilterOperations.Contains, value: searchText },
            ],
            logicalOperator: LogicalOperators.Or,
         };

         if (isValidCSharpInt(+searchText)) {
            subExpression.filterOptions.push({
               field: 'LcsAccountNumber',
               operation: FilterOperations.Contains,
               value: searchText,
            });
         }

         filterExpression.subExpressions = [subExpression];
      }

      return filterExpression;
   }

   private convertUserFilterOption(option: AccountsFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: AccountsFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }

      switch (option.type) {
         case 'account':
            return Array.isArray(option.value) ? option.value.map((v) => v.accountId) : option.value?.accountId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
         default:
            return null;
      }
   }

   private getAccountsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetAccountsVariables {
      return {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
